import React from 'react';
import MuiTab, { TabProps as MuiTabProps } from '@mui/material/Tab';
import { AbstractButton } from '../AbstractButton';
import { typedMemo } from '../../utils/helpers';
import { useTabsContext } from './Tabs';

const TabDefaultElement = 'button';
type TabDefaultElement = typeof TabDefaultElement;

type TabRenderProps = {
  /** Whether the tab is selected */
  isSelected?: boolean;

  /** Whether the tab is focused */
  isFocused?: boolean;
};

type TabOwnProps = {
  /** Whether the tab should be disabled */
  disabled?: boolean;

  /** Additional styles to apply (combined with pounce props) */
  sx?: MuiTabProps['sx'];

  /** The children of this tab. Can be a render-props pattern as well */
  children: ((renderProps: TabRenderProps) => React.ReactNode) | React.ReactNode;
};

// Publicly exposed props
export type TabProps<T extends React.ElementType = TabDefaultElement> = TabOwnProps & {
  as?: T;
} & React.ComponentPropsWithoutRef<T>;

const Tab = React.forwardRef<HTMLButtonElement, TabOwnProps & TabRenderProps>(function Tab(
  { children, isSelected, isFocused, disabled, ...rest },
  ref,
) {
  const { selectedIndex } = useTabsContext();
  const [tabIndex, setTabIndex] = React.useState<number | undefined>(undefined);

  // Get the index of this tab by counting previous tabs
  const tabRef = React.useRef<HTMLButtonElement>(null);
  React.useImperativeHandle(ref, () => tabRef.current as HTMLButtonElement);

  React.useEffect(() => {
    if (tabRef.current) {
      const parent = tabRef.current.parentElement;
      if (parent) {
        const siblings = Array.from(parent.children);
        const index = siblings.indexOf(tabRef.current);
        setTabIndex(index);
      }
    }
  }, []);

  const actualIsSelected = tabIndex !== undefined ? selectedIndex === tabIndex : isSelected;
  const actualIsFocused = isFocused || (tabIndex !== undefined && selectedIndex === tabIndex);

  // @ts-ignore
  const content =
    typeof children === 'function'
      ? children({ isSelected: actualIsSelected, isFocused: actualIsFocused })
      : children;

  return (
    <MuiTab ref={tabRef} disabled={disabled} component={AbstractButton} label={content} {...rest} />
  );
});

export default typedMemo(Tab);
